import { useState } from 'react';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import {
  DEFAULT_SERIES_FORMAT,
  VISIO_SERIES_FORMAT_MAX_LENGTH,
  VISIO_SERIES_POINTS_MAX,
  VISIO_SERIES_POINT_MAX_LENGTH,
  VISIO_SERIES_TITLE_MAX_LENGTH,
  newSeriesPointId,
  visioSeriesInputSchema,
  type VisioSeriesInput,
} from '@jeraisonne/db/visio';

const FIELD =
  'border-ink-200 text-ink-900 focus:border-primary-500 focus:ring-primary-500/20 w-full rounded-xl border bg-white px-3 text-base focus:outline-none focus:ring-2 sm:text-sm';

/**
 * Création / édition d'une série de points à aborder : un titre, une liste
 * ordonnée de points (les questions à traiter pendant la visio, puis à retrouver
 * dans le résumé) et une consigne de forme optionnelle. Validé par le schéma Zod
 * partagé ; les points laissés vides sont écartés à l'enregistrement.
 */
export function VisioSeriesForm({
  initial,
  saving,
  error,
  onSubmit,
  onCancel,
}: {
  initial: VisioSeriesInput;
  saving: boolean;
  error: string | null;
  onSubmit: (series: VisioSeriesInput) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial.title);
  const [points, setPoints] = useState(initial.points);
  const [formatMd, setFormatMd] = useState(initial.formatMd ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  const setPointText = (id: string, text: string) =>
    setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, text } : p)));
  const removePoint = (id: string) => setPoints((prev) => prev.filter((p) => p.id !== id));
  const movePoint = (index: number, delta: -1 | 1) =>
    setPoints((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = visioSeriesInputSchema.safeParse({
      title,
      points: points.filter((p) => p.text.trim()),
      formatMd,
    });
    if (!parsed.success) {
      setLocalError(parsed.error.issues[0]?.message ?? 'Série invalide.');
      return;
    }
    setLocalError(null);
    onSubmit(parsed.data);
  }

  const shownError = localError ?? error;

  return (
    <form
      onSubmit={handleSubmit}
      className="border-ink-100 space-y-4 rounded-2xl border bg-white p-4"
    >
      <div>
        <label htmlFor="series-title" className="text-ink-700 text-xs font-semibold">
          Titre de la série
        </label>
        <input
          id="series-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={VISIO_SERIES_TITLE_MAX_LENGTH}
          placeholder="Ex. Entretien de suivi"
          className={`${FIELD} mt-1 min-h-[2.75rem]`}
        />
      </div>

      <div>
        <p className="text-ink-700 text-xs font-semibold">Points à aborder</p>
        <p className="text-ink-500 mt-0.5 text-xs">
          Ce que tu veux traiter pendant le rendez-vous — le résumé y répondra point par point, à
          partir du dialogue.
        </p>
        <ul className="mt-2 space-y-2">
          {points.map((point, index) => (
            <li key={point.id} className="flex items-center gap-1">
              <span className="text-ink-400 w-5 shrink-0 text-right text-xs">{index + 1}.</span>
              <input
                aria-label={`Point ${index + 1}`}
                value={point.text}
                onChange={(e) => setPointText(point.id, e.target.value)}
                maxLength={VISIO_SERIES_POINT_MAX_LENGTH}
                placeholder="Ex. Quels thèmes ont été abordés ?"
                className={`${FIELD} min-h-[2.75rem] min-w-0 flex-1`}
              />
              <button
                type="button"
                onClick={() => movePoint(index, -1)}
                disabled={index === 0}
                aria-label={`Monter le point ${index + 1}`}
                className="text-ink-500 flex size-11 shrink-0 items-center justify-center rounded-lg active:scale-90 disabled:opacity-30"
              >
                <ChevronUp className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => movePoint(index, 1)}
                disabled={index === points.length - 1}
                aria-label={`Descendre le point ${index + 1}`}
                className="text-ink-500 flex size-11 shrink-0 items-center justify-center rounded-lg active:scale-90 disabled:opacity-30"
              >
                <ChevronDown className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => removePoint(point.id)}
                aria-label={`Supprimer le point ${index + 1}`}
                className="text-ink-500 hover:text-accent-700 flex size-11 shrink-0 items-center justify-center rounded-lg active:scale-90"
              >
                <X className="size-4" />
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setPoints((prev) => [...prev, { id: newSeriesPointId(), text: '' }])}
          disabled={points.length >= VISIO_SERIES_POINTS_MAX}
          className="text-primary-600 mt-2 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold active:scale-95 disabled:opacity-40"
        >
          <Plus className="size-4" />
          Ajouter un point
        </button>
      </div>

      <div>
        <label htmlFor="series-format" className="text-ink-700 text-xs font-semibold">
          Forme du résumé (optionnel)
        </label>
        <textarea
          id="series-format"
          value={formatMd}
          onChange={(e) => setFormatMd(e.target.value)}
          maxLength={VISIO_SERIES_FORMAT_MAX_LENGTH}
          rows={3}
          placeholder={DEFAULT_SERIES_FORMAT}
          className={`${FIELD} mt-1 py-2`}
        />
        <p className="text-ink-400 mt-1 text-xs">
          Laisse vide pour la forme par défaut (une section par point, réponses courtes en puces).
        </p>
      </div>

      {shownError && (
        <p
          role="alert"
          className="border-accent-200 bg-accent-50 text-accent-700 rounded-xl border px-3 py-2 text-xs font-medium"
        >
          {shownError}
        </p>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
        >
          Annuler
        </Button>
        <Button
          type="submit"
          loading={saving}
          className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
        >
          Enregistrer
        </Button>
      </div>
    </form>
  );
}
